import { useCallback, useEffect, useRef, useMemo } from "react";
import ThemeConstants from "../../../../../config/theme/theme_constants";
import { useGetIncomingSession } from "../../../domain/hooks/use_get_incoming_session";
import { SyncLoader } from "react-spinners";
import CalendarElement from "./calendar_element";
import { SessionInterface } from "../../../domain/interface/session_interface";
import { useTranslation } from "../../../../../i18n/useTranslation";
import { getCurrentLocale } from '../../../../../i18n/config';


const theme = ThemeConstants();
const sessionColors = [theme.UvibesYellow, theme.UvibesBlue, theme.UvibesOrange, theme.UvibesGreen];
const getSessionColor = (index: number) => sessionColors[index % sessionColors.length];

const CalendarGrid: React.FC = () => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const { 
        allSessions, 
        isLoading, 
        error, 
        refetch, 
        fetchNextPage, 
        hasNextPage, 
        isFetchingNextPage 
    } = useGetIncomingSession();

    const sessionsByMonth = useMemo(() => {
        if (!allSessions || allSessions.length === 0) return {};

        const grouped: { [key: string]: SessionInterface[] } = {};
        
        allSessions.forEach(session => {
            const date = new Date(session.start);
            const monthName = date.toLocaleDateString(getCurrentLocale(), { 
                month: 'long', 
                year: 'numeric' 
            });
            
            if (!grouped[monthName]) {
                grouped[monthName] = [];
            }
            grouped[monthName].push(session);
        });

        return grouped;
    }, [allSessions]);

    const handleLoadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    handleLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        const loadMoreElement = loadMoreRef.current;
        if (loadMoreElement) {
            observer.observe(loadMoreElement);
        }

        return () => {
            if (loadMoreElement) {
                observer.unobserve(loadMoreElement);
            }
        };
    }, [handleLoadMore]);

    return (
        <div className="bg-white flex-1 rounded-t-[30px] px-3 py-7 flex flex-col gap-8 overflow-x-clip">
            {isLoading
                ? <SyncLoader color={theme.UvibesPink} size={24} speedMultiplier={0.5}/>
                : error !== null
                    ? <div className="flex flex-col items-center gap-5">
                        <p className="font-semibold text-center text-sm">{t('common.error.fetch_news')}</p>
                        <button
                            className="rounded-full text-white font-bold px-8 py-2.5 min-h-10.5 min-w-51 cursor-pointer"
                            style={{ backgroundColor: theme.UvibesPink }}
                            onClick={() => {refetch();}}
                            >
                            {t('common.button.retry')}
                        </button>
                    </div>
                    : allSessions.length === 0
                        ? <p className="font-semibold text-center text-sm">{t('home.calendar.empty')}</p>
                        : <div className="flex flex-col gap-6">
                            {Object.entries(sessionsByMonth).map(([monthName, sessions]) => (
                                <div key={`month-${monthName}`} className="grid grid-cols-[repeat(auto-fit,_minmax(min(177px,_100%),_177px))] gap-4 justify-center">
                                    <h3 className="text-lg font-bold capitalize col-span-full justify-self-start">
                                        {monthName}
                                    </h3>
                                    {sessions.map((session, index) => (
                                        <CalendarElement 
                                            key={session.id}
                                            {...session}
                                            color={getSessionColor(index)}
                                        />
                                    ))}
                                </div>
                            ))}
                            
                            {hasNextPage && (
                                <div ref={loadMoreRef} className="flex justify-center py-4">
                                    {isFetchingNextPage ? (
                                        <SyncLoader color={theme.UvibesPink} size={16} speedMultiplier={0.5}/>
                                    ) : (
                                        <button 
                                            onClick={handleLoadMore}
                                            className="text-sm font-medium px-6 py-2 rounded-full border border-gray-300 hover:bg-gray-50"
                                        >
                                            {t('home.calendar.load_more')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
            }
        </div>
    );
}

export default CalendarGrid;
